import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Pencil, X } from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function BookingsCalendar({ bookings, calMonth, setCalMonth, statusColor, onEdit, onCancel }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const year = calMonth.getFullYear();
  const month = calMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const bookingsByDate = {};
  bookings.forEach(b => {
    const dateStr = b.start_datetime?.slice(0, 10);
    if (!dateStr) return;
    if (!bookingsByDate[dateStr]) bookingsByDate[dateStr] = [];
    bookingsByDate[dateStr].push(b);
  });

  const prevMonth = () => setCalMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCalMonth(new Date(year, month + 1, 1));

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const getDateStr = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const selectedBookings = selectedDate ? (bookingsByDate[selectedDate] || []) : [];

  return (
    <div className="space-y-4" data-testid="rentals-calendar">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <h3 className="font-heading font-semibold text-lg">{MONTH_NAMES[month]} {year}</h3>
        <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>

      <div className="border rounded-lg overflow-hidden" data-testid="cal-grid">
        <div className="grid grid-cols-7 bg-muted/50">
          {WEEKDAYS.map(d => (
            <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-2 border-b">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            if (!day) return <div key={i} className="min-h-[72px] border-b border-r bg-muted/10" />;
            const dateStr = getDateStr(day);
            const dayBookings = bookingsByDate[dateStr] || [];
            const isToday = dateStr === todayStr;
            const isSelected = dateStr === selectedDate;
            return (
              <div
                key={i}
                className={`min-h-[72px] border-b border-r p-1 cursor-pointer transition-colors ${isSelected ? "bg-primary/5 ring-1 ring-primary/30" : "hover:bg-muted/30"} ${isToday ? "bg-yellow-50/50" : ""}`}
                onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                data-testid={`cal-day-${dateStr}`}
              >
                <div className={`text-xs font-mono mb-0.5 ${isToday ? "bg-rig-charcoal text-white w-5 h-5 rounded-full flex items-center justify-center" : "text-muted-foreground"}`}>
                  {day}
                </div>
                {dayBookings.slice(0, 3).map((b, j) => (
                  <div key={j} className={`text-[10px] leading-tight truncate rounded px-1 py-0.5 mb-0.5 ${b.status === "confirmed" ? "bg-green-100 text-green-800" : b.status === "cancelled" ? "bg-gray-100 text-gray-500 line-through" : "bg-amber-100 text-amber-800"}`}>
                    {b.start_datetime?.slice(11, 16)} {b.space_name?.split(" ")[0]}
                  </div>
                ))}
                {dayBookings.length > 3 && (
                  <div className="text-[10px] text-muted-foreground font-medium">+{dayBookings.length - 3} more</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedDate && (
        <Card className="border border-border p-4" data-testid="cal-day-detail">
          <h4 className="font-heading font-semibold text-sm mb-3">
            {new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            <Badge variant="secondary" className="ml-2 text-xs">{selectedBookings.length} booking{selectedBookings.length !== 1 ? "s" : ""}</Badge>
          </h4>
          {selectedBookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No bookings on this day.</p>
          ) : (
            <div className="space-y-2">
              {selectedBookings.map(b => (
                <div key={b.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-muted/30 border" data-testid={`cal-booking-${b.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{b.space_name}</span>
                      <Badge className={`text-[10px] rounded-sm ${statusColor(b.status)}`}>{b.status}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {b.start_datetime?.slice(11, 16)} — {b.end_datetime?.slice(11, 16)}
                      {b.meeting_name && <span className="ml-1">· {b.meeting_name}</span>}
                    </div>
                    {b.booked_for_name && <div className="text-xs text-rig-teal">For: {b.booked_for_name}</div>}
                    <div className="text-xs text-muted-foreground">By {b.user_name || b.guest_name || "—"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold">XCG {b.price?.toFixed(2)}</div>
                    {onEdit && onCancel && !["cancelled", "completed", "pending_cancellation"].includes(b.status) && (
                      <div className="flex gap-0.5 justify-end mt-1">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onEdit(b)} data-testid={`cal-edit-${b.id}`}><Pencil className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => onCancel(b.id)} data-testid={`cal-cancel-${b.id}`}><X className="h-3 w-3" /></Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
